<script>
  // The live preview beside the questions, with a tab per thing customers see:
  // - Chat: the storefront's chat widget (the web chat settings' preview page) showing the unsaved survey at the end
  //   of a chat, open on the question being edited. Display-only, like the web chat preview, so nothing typed into it
  //   reaches a real conversation.
  // - Survey page: the page email-style customers answer on (shipped's SurveyPreviewsController#show).
  // - Email: the survey email that links to it (#email), with the page form's unsaved subject and message.
  // The page and the email get the unsaved survey by a form posted into their frame; neither saves anything. Each
  // follows edits, debounced so typing a prompt doesn't re-render per keystroke.
  let { builder, chatUrl, pageUrl, emailUrl, fieldPrefix } = $props()

  const tabs = [
    {
      id: 'chat', label: 'Chat', url: chatUrl, missing: 'Set up web chat to preview the survey in chat.',
      hint: "What customers see when a chat ends, open on the question you're editing.",
    },
    {
      id: 'page', label: 'Survey page', url: pageUrl, missing: 'Save the survey to preview its page.',
      hint: 'Where customers answer on email, web form, help center and API conversations.',
    },
    {
      id: 'email', label: 'Email', url: emailUrl, missing: 'Save the survey to preview its email.',
      hint: 'The email that invites customers to the survey page, sent once the conversation is done.',
    },
  ]
  let tab = $state(chatUrl || !pageUrl ? 'chat' : 'page')
  const current = $derived(tabs.find(other => other.id === tab))

  let chatFrame = $state(null)
  let chatLoaded = $state(false)

  // The page form's email subject and message, outside the builder: followed as they're typed.
  const emailInputs = ['survey[email_subject]', 'survey[email_intro]']
    .map(name => document.querySelector(`[name="${name}"]`))
    .filter(Boolean)
  let emailCopy = $state(emailInputs.map(input => [input.name, input.value]))
  $effect(() => {
    const update = () => (emailCopy = emailInputs.map(input => [input.name, input.value]))
    emailInputs.forEach(input => input.addEventListener('input', update))
    return () => emailInputs.forEach(input => input.removeEventListener('input', update))
  })

  // Posts the unsaved survey into a tab's frame. Built on <body>: the builder sits inside the page's survey form, and
  // forms can't nest.
  function post(url, target, fields) {
    const form = Object.assign(document.createElement('form'), { method: 'post', action: url, target, hidden: true })
    fields.forEach(([name, value]) => form.append(Object.assign(document.createElement('input'), {
      type: 'hidden', name, value: value ?? '',
    })))
    document.body.append(form)
    form.submit()
    form.remove()
  }

  const chatMessage = $derived({
    type: 'shipped:preview-survey',
    survey: $state.snapshot(builder.widgetSurvey()),
    focusKey: builder.keys.get(builder.selectedUid) ?? null,
  })
  const questionFields = $derived(builder.formFields(fieldPrefix))

  $effect(() => {
    const message = chatMessage
    if (tab !== 'chat' || !chatLoaded || !chatFrame?.contentWindow) return
    const timer = setTimeout(() => chatFrame.contentWindow.postMessage(message, window.location.origin), 150)
    return () => clearTimeout(timer)
  })

  $effect(() => {
    // The survey page opens on the question being edited, like the chat preview; the email doesn't change with it.
    const fields = tab === 'email'
      ? [...emailCopy, ...questionFields]
      : [['focus', builder.keys.get(builder.selectedUid) ?? ''], ...questionFields]
    if (tab === 'chat' || !current.url) return
    const timer = setTimeout(() => post(current.url, `survey-${tab}-preview`, fields), 400)
    return () => clearTimeout(timer)
  })
</script>

<div class="mb-3 flex flex-wrap items-center justify-between gap-3">
  <h4 class="text-sm font-semibold text-gray-900">Preview</h4>
  <div class="flex w-max items-center gap-1 rounded-xl bg-gray-150 p-1 text-sm font-bold text-gray-29" role="tablist">
    {#each tabs as other (other.id)}
      <button
        type="button"
        role="tab"
        aria-selected={tab === other.id}
        class="cursor-pointer rounded-lg px-3 py-1 {tab === other.id ? 'bg-white shadow-sm' : ''}"
        onclick={() => (tab = other.id)}
      >
        {other.label}
      </button>
    {/each}
  </div>
</div>
<p class="mb-3 text-xs text-gray-500">{current.hint}</p>

<div class="flex justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-inner"
     style="height: 640px;">
  {#if !current.url}
    <p class="self-center px-6 text-center text-sm text-gray-500">{current.missing}</p>
  {/if}
  {#if chatUrl}
    <iframe
      bind:this={chatFrame}
      src={chatUrl}
      title="Survey chat preview"
      class="pointer-events-none h-full w-full border-0 {tab === 'chat' ? '' : 'hidden'}"
      onload={() => (chatLoaded = true)}
    ></iframe>
  {/if}
  {#if pageUrl}
    <iframe
      name="survey-page-preview"
      title="Survey page preview"
      class="h-full w-full border-0 {tab === 'page' ? '' : 'hidden'}"
    ></iframe>
  {/if}
  {#if emailUrl}
    <iframe
      name="survey-email-preview"
      title="Survey email preview"
      class="h-full w-full border-0 {tab === 'email' ? '' : 'hidden'}"
    ></iframe>
  {/if}
</div>
